<script setup>
import { computed, useAttrs } from 'vue';

defineProps({
  indeterminate: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['change']);

defineOptions({ inheritAttrs: false });

// Naming and form attributes belong to the real input; class, style and data-*
// stay on the wrapper, as they did before the input received attrs.
const INPUT_ATTR = /^(id|name|value|required|form|tabindex|autofocus|aria-.+)$/;
const attrs = useAttrs();
const inputAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([k]) => INPUT_ATTR.test(k)))
);
const wrapperAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([k]) => !INPUT_ATTR.test(k)))
);

const modelValue = defineModel('modelValue', {
  type: Boolean,
  default: false,
});

const handleChange = event => {
  modelValue.value = event.target.checked;
  emit('change', event);
};
</script>

<template>
  <div class="relative w-4 h-4" v-bind="wrapperAttrs">
    <input
      v-bind="inputAttrs"
      :checked="modelValue"
      :indeterminate="indeterminate"
      type="checkbox"
      :disabled="disabled"
      class="peer absolute start-1/2 top-1/2 z-10 h-4 w-4 ltr:-translate-x-1/2 rtl:translate-x-1/2 -translate-y-1/2 appearance-none border-0 bg-transparent focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-1 max-sm:h-11 max-sm:w-11 cursor-pointer disabled:cursor-not-allowed"
      @change="handleChange"
    />
    <span
      class="pointer-events-none absolute inset-0 rounded border border-n-slate-6 bg-transparent transition-all duration-200 peer-checked:border-n-brand peer-checked:bg-n-brand dark:border-n-slate-6 dark:peer-checked:border-n-brand peer-indeterminate:border-n-brand peer-indeterminate:bg-n-brand peer-disabled:opacity-50 peer-hover:bg-n-blue-border"
    />
    <!-- Checkmark SVG -->
    <svg
      viewBox="0 0 14 14"
      fill="none"
      class="pointer-events-none absolute w-3.5 h-3.5 z-20 stroke-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <path
        d="M3 8L6 11L11 3.5"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
    <!-- Minus/Indeterminate SVG -->
    <svg
      viewBox="0 0 14 14"
      fill="none"
      class="pointer-events-none absolute w-3.5 h-3.5 z-20 stroke-white opacity-0 peer-indeterminate:opacity-100 transition-opacity duration-200 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
    >
      <path
        d="M3 7L11 7"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
</template>
